import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "antd";
import { SendOutlined } from "@ant-design/icons";

/**
 * DynamicTextArea 组件的属性接口
 * 支持受控和非受控两种模式，具有动态文案动画效果
 */
export interface DynamicTextAreaProps {
  /** 动态展示的文案数组，会循环播放打字机效果 */
  texts: string[];
  /** 输入框占位符文本 */
  placeholder?: string;
  /** 打字机动画的速度，单位毫秒，数值越小速度越快 */
  animationSpeed?: number;
  /** 是否自动播放动态文案动画 */
  autoPlay?: boolean;
  /** 输入框的值，用于受控模式 */
  value?: string;
  /** 输入框值变化时的回调函数，用于受控模式 */
  onChange?: (value: string) => void;
  /** 点击提交按钮时的回调函数 */
  onSubmit?: (value: string) => void;
  /** 最大输入字符数限制 */
  maxLength?: number;
  /** 是否在底部显示字数统计 */
  showCount?: boolean;
  /** 提交按钮的文本内容 */
  submitText?: string;
  /** 是否禁用整个组件 */
  disabled?: boolean;
  /** 文本域的行数，影响初始高度 */
  rows?: number;
  /** 自定义样式对象 */
  style?: React.CSSProperties;
  /** 其他原生 textarea 属性 */
  [key: string]: unknown;
}

type DocgenInfo = {
  displayName: string;
  description: string;
  props: Record<string, {
    name: string;
    required: boolean;
    description: string;
    type: { name: string; raw: string };
    defaultValue?: { value: string };
  }>;
};

type ComponentWithDocgen = React.FC<DynamicTextAreaProps> & { __docgenInfo?: DocgenInfo };

/**
 * 动态文案文本域组件
 * 
 * 功能特性：
 * 1. 支持受控和非受控两种模式
 * 2. 动态文案打字机效果，循环播放预设文案
 * 3. 内置提交按钮，位于文本框右下角
 * 4. 支持字数限制和字数统计显示
 * 5. 支持键盘快捷键提交（Ctrl+Enter 或 Cmd+Enter）
 * 6. 智能占位符显示，避免与动态文案重叠
 * 
 * @param props - 组件属性
 * @returns JSX 元素
 */
export const DynamicTextArea: React.FC<DynamicTextAreaProps> = ({
  texts = [], // 动态文案数组，默认为空数组
  placeholder = "请输入内容", // 占位符文本
  animationSpeed = 100, // 打字机动画速度，毫秒
  autoPlay = true, // 是否自动播放动画
  value = "", // 受控模式的值
  onChange, // 受控模式的变化回调
  onSubmit, // 提交回调
  maxLength = 500, // 最大字符数限制
  showCount = true, // 是否显示字数统计
  submitText = "发送", // 提交按钮文本
  disabled = false, // 是否禁用组件
  rows = 4, // 文本域行数
  style, // 自定义样式
  ...otherProps // 其他原生属性
}) => {
  // ==================== 状态管理 ====================
  
  /** 当前显示的动态文案内容 */
  const [currentText, setCurrentText] = useState("");
  
  /** 当前播放的文案在数组中的索引 */
  const [currentIndex, setCurrentIndex] = useState(0);
  
  /** 是否正在播放动画（用于控制动画循环） */
  const [isAnimating, setIsAnimating] = useState(false);
  
  /** 文本域是否获得焦点 */
  const [isFocused, setIsFocused] = useState(false);
  
  /** 是否正在打字（用于显示光标效果） */
  const [isTyping, setIsTyping] = useState(false);
  
  /** 非受控模式下的内部状态值 */
  const [internalValue, setInternalValue] = useState(value || "");
  
  // ==================== Refs 引用 ====================
  
  /** 动画定时器引用，用于清除动画 */
  const animationRef = useRef<number | undefined>(undefined);
  
  /** 打字机效果定时器引用，用于清除打字动画 */
  const typingRef = useRef<number | undefined>(undefined);
  
  /** 文案数组的引用，避免闭包问题 */
  const textsRef = useRef(texts);
  
  /** 当前索引的引用，避免闭包问题 */
  const currentIndexRef = useRef(currentIndex);
  
  /** 焦点状态的引用，避免闭包问题 */
  const isFocusedRef = useRef(isFocused);
  
  /** 当前文案的引用，避免闭包问题 */
  const currentTextRef = useRef(currentText);
  
  /** 文本域 DOM 元素的引用 */
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ==================== 副作用更新 ====================
  
  /**
   * 更新所有 refs 的值，确保在回调函数中能获取到最新的状态
   * 这是为了避免闭包陷阱，确保动画函数能访问到最新的状态值
   */
  useEffect(() => {
    textsRef.current = texts;
    currentIndexRef.current = currentIndex;
    isFocusedRef.current = isFocused;
    currentTextRef.current = currentText;
  });

  // ==================== 动画效果函数 ====================
  
  /**
   * 打字机效果函数
   * 逐字符显示文本，模拟真实的打字过程
   * 
   * @param text - 要显示的完整文本
   * @param callback - 打字完成后的回调函数
   */
  const typeText = useCallback((text: string, callback?: () => void) => {
    let index = 0; // 当前显示的字符索引
    setIsTyping(true); // 开始打字状态，显示光标
    
    const typeChar = () => {
      if (index < text.length) {
        // 显示从开始到当前索引的字符
        setCurrentText(text.slice(0, index + 1));
        index++;
        // 设置定时器，继续下一个字符
        typingRef.current = window.setTimeout(typeChar, animationSpeed);
      } else {
        // 打字完成
        setIsTyping(false); // 隐藏光标
        callback?.(); // 执行完成回调
      }
    };
    
    typeChar(); // 开始打字
  }, [animationSpeed]);

  /**
   * 删除文字效果函数
   * 逐字符删除文本，模拟删除过程
   * 
   * @param callback - 删除完成后的回调函数
   */
  const deleteText = useCallback((callback?: () => void) => {
    let index = currentTextRef.current.length; // 从最后一个字符开始删除
    
    const deleteChar = () => {
      if (index > 0) {
        // 删除一个字符
        setCurrentText(currentTextRef.current.slice(0, index - 1));
        index--;
        // 设置定时器，继续删除下一个字符（删除速度比打字快一倍）
        typingRef.current = window.setTimeout(deleteChar, animationSpeed / 2);
      } else {
        // 删除完成
        callback?.(); // 执行完成回调
      }
    };
    
    deleteChar(); // 开始删除
  }, [animationSpeed]);

  /**
   * 循环播放动画函数
   * 控制整个动画流程：删除当前文案 -> 输入新文案 -> 等待 -> 循环
   * 
   * 动画流程：
   * 1. 检查是否有文案且未获得焦点
   * 2. 如果有当前文案，先删除
   * 3. 删除完成后，输入新的文案
   * 4. 输入完成后，等待2秒
   * 5. 切换到下一个文案，重新开始循环
   */
  const playAnimation = useCallback(() => {
    // 如果没有文案或已获得焦点，则不播放动画
    if (textsRef.current.length === 0 || isFocusedRef.current) return;
    
    setIsAnimating(true); // 标记正在播放动画
    
    const currentTextItem = textsRef.current[currentIndexRef.current]; // 获取当前要显示的文案
    
    // 如果当前有文案，先删除再输入新文案
    if (currentTextRef.current) {
      deleteText(() => {
        // 删除完成后的回调：开始输入新文案
        typeText(currentTextItem, () => {
          // 输入完成后的回调：等待2秒后切换到下一个文案
          animationRef.current = window.setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % textsRef.current.length); // 循环到下一个文案
            setIsAnimating(false); // 标记动画结束
          }, 2000); // 等待2秒
        });
      });
    } else {
      // 如果当前没有文案，直接输入新文案
      typeText(currentTextItem, () => {
        animationRef.current = window.setTimeout(() => {
          setCurrentIndex((prev) => (prev + 1) % textsRef.current.length); // 循环到下一个文案
          setIsAnimating(false); // 标记动画结束
        }, 2000); // 等待2秒
      });
    }
  }, [deleteText, typeText]);

  // 开始动画
  useEffect(() => {
    if (autoPlay && texts.length > 0 && !isFocused) {
      playAnimation();
    }
    
    return () => {
      if (animationRef.current) {
        window.clearTimeout(animationRef.current);
      }
      if (typingRef.current) {
        window.clearTimeout(typingRef.current);
      }
    };
  }, [currentIndex, isFocused, autoPlay, texts.length, playAnimation]);

  // 当索引变化时重新播放动画
  useEffect(() => {
    if (!isAnimating && autoPlay && texts.length > 0 && !isFocused) {
      playAnimation();
    }
  }, [currentIndex, isAnimating, autoPlay, texts.length, isFocused, playAnimation]);

  // 处理焦点事件
  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    // 清除所有动画
    if (animationRef.current) {
      window.clearTimeout(animationRef.current);
    }
    if (typingRef.current) {
      window.clearTimeout(typingRef.current);
    }
    setIsAnimating(false);
    setIsTyping(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (otherProps as any).onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    // 如果输入框为空，重新开始动画
    if (!value && texts.length > 0) {
      setCurrentText("");
      setCurrentIndex(0);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (otherProps as any).onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) {
      return; // 超过最大长度时不更新
    }
    
    // 如果是受控模式，调用外部 onChange
    if (onChange) {
      onChange(newValue);
    } else {
      // 非受控模式，更新内部状态
      setInternalValue(newValue);
    }
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (otherProps as any).onChange?.(e);
  };

  const handleSubmit = () => {
    const currentValue = onChange ? value : internalValue;
    if (currentValue.trim() && !disabled) {
      onSubmit?.(currentValue);
      // 非受控模式下，提交后清空内部状态
      if (!onChange) {
        setInternalValue("");
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter 或 Cmd+Enter 提交
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (otherProps as any).onKeyDown?.(e);
  };

  const currentValue = onChange ? value : internalValue;
  const isSubmitDisabled = disabled || !currentValue.trim() || (maxLength ? currentValue.length > maxLength : false);

  return (
    <div style={{ position: "relative", ...style }}>
      <div style={{ position: "relative" }}>
        <textarea
          ref={textareaRef}
          value={onChange ? value : internalValue}
          onChange={handleChange}
          onFocus={(e) => {
            e.target.style.borderColor = "#1890ff";
            e.target.style.boxShadow = "0 0 0 2px rgba(24, 144, 255, 0.2)";
            handleFocus(e);
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#d9d9d9";
            e.target.style.boxShadow = "none";
            handleBlur(e);
          }}
          onKeyDown={handleKeyDown}
          placeholder={isFocused || currentValue ? placeholder : ""} // 只在聚焦或有值时显示 placeholder
          maxLength={maxLength}
          rows={rows}
          disabled={disabled}
          style={{
            width: "100%",
            minHeight: `${rows * 24 + 16}px`,
            padding: "8px 60px 8px 12px", // 右侧留出按钮空间
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            fontSize: "14px",
            lineHeight: "1.5",
            fontFamily: "inherit",
            resize: "vertical",
            outline: "none",
            transition: "border-color 0.3s, box-shadow 0.3s",
            backgroundColor: "#fff",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(otherProps as any).style,
          }}
          {...otherProps}
        />
        
        {/* 动态文字覆盖层 - 只在未聚焦且无值时显示 */}
        {!isFocused && !currentValue && currentText && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "flex-start",
              paddingTop: 8,
              paddingLeft: 12,
              paddingRight: 12,
              color: "#bfbfbf",
              pointerEvents: "none",
              zIndex: 2,
              fontSize: 14,
              fontFamily: "inherit",
              lineHeight: "1.5",
            }}
          >
            <span>{currentText}</span>
            {/* 光标效果 */}
            {isTyping && (
              <span
                style={{
                  display: "inline-block",
                  width: 1,
                  height: "1.2em",
                  backgroundColor: "#1890ff",
                  marginLeft: 2,
                  animation: "blink 1s infinite",
                }}
              />
            )}
          </div>
        )}
        
        {/* 提交按钮 */}
        <div
          style={{
            position: "absolute",
            bottom: 8,
            right: 8,
            zIndex: 3,
          }}
        >
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            style={{
              borderRadius: 6,
              minWidth: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {submitText}
          </Button>
        </div>
      </div>
      
      {/* 字数统计 */}
      {showCount && (
        <div
          style={{
            marginTop: 4,
            textAlign: "right",
            fontSize: "12px",
            color: "#999",
          }}
        >
          {currentValue.length}/{maxLength}
        </div>
      )}
      
      {/* 添加光标闪烁动画 */}
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `}
      </style>
    </div>
  );
};

// 手动添加 docgen 信息
(DynamicTextArea as ComponentWithDocgen).__docgenInfo = {
  displayName: "DynamicTextArea",
  description: "动态文案文本域组件，支持流式文字动画效果",
  props: {
    texts: {
      name: "texts",
      required: true,
      description: "动态展示的文案数组",
      type: { name: "string[]", raw: "string[]" }
    },
    placeholder: {
      name: "placeholder",
      required: false,
      description: "输入框占位符",
      type: { name: "string", raw: "string" },
      defaultValue: { value: '"请输入内容"' }
    },
    animationSpeed: {
      name: "animationSpeed",
      required: false,
      description: "动画速度（毫秒）",
      type: { name: "number", raw: "number" },
      defaultValue: { value: "100" }
    },
    autoPlay: {
      name: "autoPlay",
      required: false,
      description: "是否自动播放动画",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    value: {
      name: "value",
      required: false,
      description: "输入框值",
      type: { name: "string", raw: "string" }
    },
    onChange: {
      name: "onChange",
      required: false,
      description: "输入框变化回调",
      type: { name: "function", raw: "(value: string) => void" }
    },
    onSubmit: {
      name: "onSubmit",
      required: false,
      description: "提交回调",
      type: { name: "function", raw: "(value: string) => void" }
    },
    maxLength: {
      name: "maxLength",
      required: false,
      description: "最大输入字数",
      type: { name: "number", raw: "number" },
      defaultValue: { value: "500" }
    },
    showCount: {
      name: "showCount",
      required: false,
      description: "是否显示字数统计",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    submitText: {
      name: "submitText",
      required: false,
      description: "提交按钮文本",
      type: { name: "string", raw: "string" },
      defaultValue: { value: '"发送"' }
    },
    disabled: {
      name: "disabled",
      required: false,
      description: "是否禁用提交按钮",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "false" }
    },
    rows: {
      name: "rows",
      required: false,
      description: "文本域行数",
      type: { name: "number", raw: "number" },
      defaultValue: { value: "4" }
    }
  }
};